import { useState } from "react";

import { Button } from "../../components/ui/Controls";
import { AlertBanner, InlineResult, StatusBadge } from "../../components/ui/Feedback";
import {
  DestructiveConfirmDialog,
  ReauthenticationDialog,
  type ReauthenticationCredentials,
} from "../../components/ui/SecurityDialogs";
import { apiErrorMessage, useAuth } from "../../lib/auth";
import { operationsApi, securityApi, type OperationsProjection } from "../../lib/api";

export function IncidentPanel({
  projectId,
  projections,
}: {
  readonly projectId: string;
  readonly projections: readonly OperationsProjection[];
}) {
  const auth = useAuth();
  const [target, setTarget] = useState<OperationsProjection>();
  const [reauthenticationId, setReauthenticationId] = useState<string>();
  const [error, setError] = useState<string>();
  const [jobId, setJobId] = useState<string>();
  const [busy, setBusy] = useState(false);
  const incidents = projections.flatMap((projection) =>
    projection.incidents.map((incident) => ({ ...incident, projection })),
  );
  const canRestart = auth.identity?.permissions.includes("deployments:cancel") === true;

  const verify = async (credentials: ReauthenticationCredentials) => {
    setBusy(true);
    setError(undefined);
    try {
      const result = await securityApi.reauthenticate({
        password: credentials.password,
        ...(credentials.secondFactor === undefined
          ? {}
          : {
              secondFactor: /^\d{6}$/u.test(credentials.secondFactor)
                ? { method: "totp" as const, code: credentials.secondFactor }
                : { method: "recovery_code" as const, code: credentials.secondFactor },
            }),
      });
      setReauthenticationId(result.reauthenticationId);
    } catch (caught) {
      setError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  const restart = async () => {
    if (target === undefined || reauthenticationId === undefined) return;
    setBusy(true);
    setError(undefined);
    try {
      const response = await operationsApi.restart({
        projectId,
        hostId: target.hostId,
        workloadId: target.workloadId,
        confirmationWorkloadId: target.workloadId,
        reauthenticationId,
      });
      setJobId(response.jobId);
      setTarget(undefined);
      setReauthenticationId(undefined);
    } catch (caught) {
      setError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="incident-heading" className="card stack">
      <div>
        <p className="eyebrow">Olaylar ve kurtarma</p>
        <h2 id="incident-heading">Doğrulanmış olaylar</h2>
      </div>
      {incidents.length === 0 ? (
        <p>Bu gözlemlerde açık veya çözülmüş olay kaydı yok.</p>
      ) : (
        <ul className="stack" aria-label="Olay listesi">
          {incidents.map(({ projection, ...incident }) => (
            <li key={incident.id} className="split-row">
              <span>
                <strong>{incident.reason}</strong>
                <br />
                <time dateTime={incident.lastSeenAt}>
                  Son gözlem: {new Date(incident.lastSeenAt).toLocaleString("tr-TR")}
                </time>
              </span>
              <StatusBadge
                status={incident.state === "open" ? "warning" : "succeeded"}
                label={incident.state === "open" ? "Açık" : "Çözüldü"}
              />
              {canRestart && projection.freshness === "confirmed" ? (
                <Button onClick={() => setTarget(projection)} variant="destructive">
                  Bu iş yükünü yeniden başlat
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {canRestart ? (
        <div className="stack">
          <h3>Doğrulanmış iş yükleri</h3>
          {projections
            .filter((projection) => projection.freshness === "confirmed")
            .map((projection) => (
              <div className="split-row" key={projection.workloadId}>
                <code>{projection.workloadId}</code>
                <Button onClick={() => setTarget(projection)} variant="destructive">
                  Bu iş yükünü yeniden başlat
                </Button>
              </div>
            ))}
        </div>
      ) : null}
      {jobId === undefined ? null : (
        <InlineResult requestId={jobId} tone="info">
          Yeniden başlatma isteği kabul edildi. İşin sonucu doğrulanana kadar tamamlandı sayılmaz.
        </InlineResult>
      )}
      {target === undefined ? null : reauthenticationId === undefined ? (
        <ReauthenticationDialog
          actionName="iş yükünü yeniden başlat"
          error={error}
          onOpenChange={(open) => {
            if (!open) setTarget(undefined);
          }}
          onVerify={verify}
          open
          requireSecondFactor={auth.identity?.user.totpEnabled === true}
          submitting={busy}
        />
      ) : (
        <DestructiveConfirmDialog
          confirmationLabel={target.workloadId}
          consequence="Bu iş yükü yeniden başlatma için kuyruğa alınacak. Sunucu yalnızca bu tam kimliği doğrularsa işlem uygulanır."
          error={error}
          finalAction="Yeniden başlatmayı iste"
          name="İş yükünü yeniden başlat"
          onConfirm={restart}
          onOpenChange={(open) => {
            if (!open) {
              setTarget(undefined);
              setReauthenticationId(undefined);
            }
          }}
          open
          submitting={busy}
        />
      )}
      {error === undefined || target !== undefined ? null : (
        <AlertBanner heading="Yeniden başlatma isteği gönderilemedi" tone="error">
          <p>{error}</p>
        </AlertBanner>
      )}
    </section>
  );
}
